export interface FormDataInput {
    productType: ProductType | undefined | null;
    donationContext: DonationContextEntries | undefined;
    formSettings?: FormSettings | undefined | null;
    userSettings?: UserSettings | undefined;
    additionalDocTypeParameters?: AdditionalDocTypeParameters | undefined | null;
    donationProductParameters?: DonationProductParameters | undefined | null;
    onlyNameAndEmailForm: boolean;
    showOptionalInformation: boolean;
    showGiftDonation: boolean;
    showNewsletterRegistration: boolean;
    sessionFormData?: FormData;
}

export enum DonationContext {
    unique = "unique",
    monthly = "monthly",
    childSponsorship = "childSponsorship",
    villageInGermanySponsorship = "villageInGermanySponsorship",
    villageAbroadSponsorship = "villageAbroadSponsorship",
}

export type DonationProductParameters = {
    productId: string;
    productGreeting: string;
    productQuantity: number;
    productFirstname: string;
    productSurname: string;
    productTotal: number;
    productCertificateId: string;
    productArticleNumber: string;
}

export type AdditionalDocTypeParameters = {
    actionId?: string;
    cmCsId?: string;
}

export type UserSettings = {
    amount?: number;
    priority?: string;
    donationType?: DonationType;
    donationRhythm?: DonationRhythm;
    sponsorshipType?: SponsorshipType;
    gift?: string;
}

export type FormSettings = {
    formHeadline: string
    paymentHeadline: string
    personalDataHeadline: string
    optionalLabel: string
    newsletterAgree: string
    privacyInfo: string
    submit_hint: SubmitHint
    payment_hint: PaymentHint,
    payment_method: Partial<Record<PaymentMethod, boolean>>,
    agreementText: string,
    privacyUrl: string,
    saferPayAccount: string,
    showOptionalInformation?: boolean;
    showGiftDonation?: boolean;
    showNewsletterRegistration?: boolean;
}

export enum DonationQueryParams {
    amount = 'amount',
    donationType = 'donationType',
    donationRhythm = 'donationRhythm',
    priority = 'priority',
    sponsorshipType = 'sponsorshipType',
    contentIdForSettings = 'cmCsId',
    contentIdForProduct = 'cmPId',
    queryType = 'qt',
    productQuantity = 'productQuantity',
    productGreeting = 'productGreeting',
    productFirstname = 'productFirstname',
    productSurname = 'productSurname',
    gift = 'gift',
}

export type DonationContextEntries = Partial<Record<DonationContext, DonationContextProperties>>

export type DonationContextProperties = {
    headline?: string;
    subheadline?: string;
    amounts: Array<number>,
    description: string,
    giftLabel?: string,
    minAmount?: number,
    pdfCertificate?: string,
    priorities: Array<string>,
    priorityLabel?: string,
    recipientDataIntroText?: string
    defaultAmount?: number;
    buttonLabel?: string;
    isSingleAmountForm?: boolean;
}

export type ProductTypeSettings = {
    donationType?: string;
    donationRhythm?: string;
    sponsorshipType?: string;
}

export type DonationProductFormData = {
    productGreeting: string;
    productQuantity: number;
    productFirstname: string;
    productSurname: string;
    productId: string;
    cmPId: string;
}

export type FormData = {
    CampaignID: string;
    ChannelID: string;
    actionId?: string;
    accountholder?: string;
    amount: number;
    bic: string;
    birthday: string;
    city: string;
    companyName: string;
    country: string;
    csrfToken: string;
    donationRhythm: any;
    donationType: DonationType | null;
    donatorType: string;
    email: string;
    extraAddressLine: string;
    firstname: string;
    gift: boolean;
    giftCertificate: string;
    giftCertificatePdfData?: { content: any, icon: string, background: string };
    giftCity: string;
    giftCompanyName: string;
    giftCountry: string;
    giftDonatorType: string;
    giftExtraAddressLine: string;
    giftFirstname: string;
    giftPersonalGreeting: string;
    giftSalutationType: string;
    giftStreet: string;
    giftSurname: string;
    giftZip: string;
    iban: string;
    id: string;
    messageToSos: string;
    newsletter: boolean;
    originUrl?: string;
    paymentMethod: PaymentMethod;
    phone?: string;
    priority: string;
    salutation: string;
    sponsorshipType: SponsorshipType | null;
    street: string;
    surname: string;
    transactionReferenceNumber: string;
    zip: string;
    productGreeting?: string;
    productQuantity?: number;
    productFirstname?: string;
    productSurname?: string;
    productTotal?: number;
    productId?: string;
    productCertificate?: string;
    cmCsId?: string;
}

export interface ProductType {
    donationType: Array<DonationType>;
    donationRhythm: Array<DonationRhythm>;
    sponsorshipType?: Array<SponsorshipType>;
}

export enum DonationType {
    Donation = 0,
    Sponsorship = 1,
    Product = 2,
}

export enum DonationRhythm {
    Unique = 0,
    Monthly = 1
}

export enum SponsorshipType {
    ChildSponsorship = 3,
    VillageSponsorshipInGermany = 4,
    VillageSponsorshipAbroad = 5,
}

export enum PaymentMethod {
    SEPA = "sepa",
    Paypal = "paypal",
    PaypalSubscription = "paypal_subscription",
    Card = "card",
    ApplePay = "applepay",
    GooglePay = "googlepay",
}

export type PaymentHint = {
    sepa?: string;
    card?: string;
    applepay?: string;
    googlepay?: string;
    paypal?: string;
    paypal_subscription?: string;
};

export type SubmitHint = {
    sepa?: string;
    card?: string;
    applepay?: string;
    googlepay?: string;
    paypal?: string;
    paypal_subscription?: string;
};

export enum SalutationType {
    Male = "Male",
    Female = "Female",
    Divers = "Divers",
    Family = "Family",
}

export enum DonatorType {
    PrivatePerson = "PrivatePerson",
    Company = "Company",
}

export enum SubmitError {
    UNKNOWN = "unknown",
    SEPA_ERROR = "sepa_error",
    PAYPAL_ERROR = "paypal_error",
    PAYPAL_CANCELLED = "paypal_cancelled",
    CARD_ERROR = "card_error",
    APPLEPAY_ERROR = "applepay_error",
    GOOGLEPAY_ERROR = "googlepay_error",
    TWICE_SUBMITTED = "twice_submitted",
    TOO_MANY_REQUESTS = "too_many_requests",
}

export class TransactionData {
    donationType: number;
    sponsorshipType: number;
    donationRhythm: number;
    donatorType: string;
    gift: boolean;
    newsletter: boolean;
    paymentMethod: string;
    amount: number;
    priority: string;
    quantity: number;
    itemId: string | undefined;

    constructor(donationType: number,
                sponsorshipType: number,
                donationRhythm: number,
                donatorType: string,
                gift: boolean,
                newsletter: boolean,
                paymentMethod: string,
                amount: number,
                priority: string,
                quantity: number,
                itemId: string | undefined) {
        this.donationType = donationType;
        this.sponsorshipType = sponsorshipType;
        this.donationRhythm = donationRhythm;
        this.donatorType = donatorType;
        this.gift = gift;
        this.newsletter = newsletter;
        this.paymentMethod = paymentMethod;
        this.amount = amount;
        this.priority = priority;
        this.quantity = quantity;
        this.itemId = itemId;
    }
}

export type DonationCardVariant = "form" | "card";
